import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [statusMsg, setStatusMsg] = useState("");

  const handleSubscribe = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const form = e.currentTarget;
      const honeypot = (form.elements.namedItem("website_alt") as HTMLInputElement)?.value?.trim();
      if (honeypot) {
        setStatus("success");
        setStatusMsg("Thank you for subscribing!");
        return;
      }

      try {
        const formData = new FormData(form);
        const res = await fetch("https://readdy.ai/api/form/d95ru1d7gtqne2as9mrg", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        });
        const responseText = await res.text();
        let parsed: Record<string, unknown> = {};
        try {
          parsed = JSON.parse(responseText);
        } catch {
          // ignore parse errors
        }
        if (res.ok && (parsed as { code?: string })?.code === "OK") {
          setStatus("success");
          setStatusMsg("Thank you for subscribing! Check your inbox soon.");
          setEmail("");
        } else {
          const msg =
            (parsed as { meta?: { message?: string } })?.meta?.message ||
            (parsed as { message?: string })?.message ||
            (parsed as { meta?: { detail?: string } })?.meta?.detail ||
            responseText ||
            "Something went wrong. Please try again.";
          setStatus("error");
          setStatusMsg(msg);
        }
      } catch {
        setStatus("error");
        setStatusMsg("Network error. Please check your connection.");
      }
    },
    []
  );

  return (
    <footer className="bg-[#3A4F63] text-background-50">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          <div className="lg:w-[40%]">
            <Link
              to="/"
              className="text-[18px] font-semibold tracking-[0.02em] font-heading inline-flex items-center gap-1.5"
            >
              CSSSpreadsheet<span className="text-accent-400">.</span>living
            </Link>
            <p className="mt-6 text-[15px] text-background-50/70 leading-relaxed max-w-[340px]">
              Your gateway to the best CSSBuy spreadsheet collections, agent shopping guides, and verified product finds.
            </p>

            <form
              onSubmit={handleSubscribe}
              data-readdy-form
              className="mt-8 flex items-center gap-2"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="h-12 px-5 bg-transparent border border-background-50/25 rounded-full text-[14px] text-background-50 placeholder:text-background-50/40 outline-none focus:border-accent-400 transition-colors duration-200 flex-1 max-w-[280px]"
              />
              <button
                type="submit"
                className="h-12 w-12 flex items-center justify-center rounded-full border border-background-50/25 hover:border-accent-400 hover:bg-accent-500/20 transition-all duration-200 flex-shrink-0 cursor-pointer"
              >
                <i className="ri-notification-3-line text-lg text-background-50 w-5 h-5 flex items-center justify-center"></i>
              </button>
            </form>
            {status !== "idle" && (
              <p
                className={`mt-3 text-[13px] ${
                  status === "success" ? "text-accent-300" : "text-red-300"
                }`}
              >
                {statusMsg}
              </p>
            )}
          </div>

          <div className="lg:w-[60%] grid grid-cols-2 sm:grid-cols-3 gap-8">
            <div>
              <h4 className="text-[15px] font-semibold mb-5">Quick Links</h4>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/"
                    className="text-[13px] text-background-50/60 hover:text-background-50 transition-colors duration-200"
                  >
                    Back to Home
                  </Link>
                </li>
                <li>
                  <a
                    href="#categories"
                    className="text-[13px] text-background-50/60 hover:text-background-50 transition-colors duration-200"
                  >
                    Categories
                  </a>
                </li>
                <li>
                  <a
                    href="#guides"
                    className="text-[13px] text-background-50/60 hover:text-background-50 transition-colors duration-200"
                  >
                    Guides
                  </a>
                </li>
                <li>
                  <a
                    href="#explore"
                    className="text-[13px] text-background-50/60 hover:text-background-50 transition-colors duration-200"
                  >
                    Explore
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-[15px] font-semibold mb-5">Popular Categories</h4>
              <ul className="space-y-3">
                {categories.slice(0, 6).map((cat) => (
                  <li key={cat.name}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-[13px] text-background-50/60 hover:text-background-50 transition-colors duration-200"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-[15px] font-semibold mb-5">Main Site</h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-[13px] text-background-50/60 hover:text-background-50 transition-colors duration-200"
                  >
                    cssbuy sheet
                  </a>
                </li>
                <li>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-[13px] text-background-50/60 hover:text-background-50 transition-colors duration-200"
                  >
                    buy it now
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-background-50/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[12px] text-background-50/50">
            &copy; {new Date().getFullYear()} CSSSpreadsheet.living. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="#"
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-background-50/20 hover:border-background-50/40 transition-colors duration-200"
              aria-label="Twitter"
            >
              <i className="ri-twitter-x-line text-background-50/60 text-sm w-4 h-4 flex items-center justify-center"></i>
            </a>
            <a
              href="#"
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-background-50/20 hover:border-background-50/40 transition-colors duration-200"
              aria-label="Reddit"
            >
              <i className="ri-reddit-line text-background-50/60 text-sm w-4 h-4 flex items-center justify-center"></i>
            </a>
            <a
              href="#"
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-background-50/20 hover:border-background-50/40 transition-colors duration-200"
              aria-label="Discord"
            >
              <i className="ri-discord-line text-background-50/60 text-sm w-4 h-4 flex items-center justify-center"></i>
            </a>
            <a
              href="#"
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-background-50/20 hover:border-background-50/40 transition-colors duration-200"
              aria-label="GitHub"
            >
              <i className="ri-github-line text-background-50/60 text-sm w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>
          <a href="#" className="text-[12px] text-background-50/50 hover:text-background-50/80 transition-colors duration-200">
            Privacy Policy
          </a>
        </div>
      </div>
    </footer>
  );
}